import {storeToRefs} from 'pinia';
import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
import {useShoppingListsStore} from '~/stores/shoppingLists';
import type {ShoppingListAddMethod} from '~/utils/tracking';

interface Options {
	product: MaybeRefOrGetter<ProductDetail | undefined>
	disabled?: MaybeRefOrGetter<boolean>
	method?: ShoppingListAddMethod
	onListChange?: (amount: number) => void
}

export default function useProductShoppingListStepper(options: Options) {
	const shoppingListsStore = useShoppingListsStore();
	const {selectedShoppingList} = storeToRefs(shoppingListsStore);

	const product = computed(() => toValue(options.product));
	const disabled = computed(() => toValue(options.disabled) ?? false);
	const isTextItem = computed(() => !product.value?.vanr);

	const {amount, pending, add, remove, currentItem} = useShoppingListProduct(() => product.value, {method: options.method});

	const stepperAmount = ref(0);
	const amountUpdatePending = ref(false);
	const isAmountDirty = ref(false);

	const isLocked = computed(() => disabled.value || pending.value || amountUpdatePending.value);

	watch(amount, (value) => {
		if (!amountUpdatePending.value && !isAmountDirty.value) {
			stepperAmount.value = value;
		}
	}, {immediate: true});

	watch(amount, (value) => {
		if (value === 0) {
			options.onListChange?.(value);
		}
	});

	watch(selectedShoppingList, () => {
		cancelAmountUpdate();
		isAmountDirty.value = false;
		stepperAmount.value = amount.value;
		options.onListChange?.(amount.value);
	});

	const flushAmountUpdate = async () => {
		const item = currentItem.value;
		const listId = selectedShoppingList.value;
		const value = stepperAmount.value;

		if (!item || listId == null) {
			isAmountDirty.value = false;
			stepperAmount.value = amount.value;
			return;
		}

		if (value === amount.value) {
			isAmountDirty.value = false;
			return;
		}

		amountUpdatePending.value = true;
		try {
			await shoppingListsStore.setAmountShoppingListItem(String(listId), item.ID, {
				Text: item.Text,
				Amount: value,
			});
		} catch {
			stepperAmount.value = amount.value;
		} finally {
			amountUpdatePending.value = false;
			isAmountDirty.value = false;
			stepperAmount.value = amount.value;
		}
	};

	let amountUpdateTimer: ReturnType<typeof setTimeout> | null = null;

	function scheduleAmountUpdate() {
		if (amountUpdateTimer) {
			clearTimeout(amountUpdateTimer);
		}
		amountUpdateTimer = setTimeout(() => {
			amountUpdateTimer = null;
			flushAmountUpdate();
		}, 400);
	}

	function cancelAmountUpdate() {
		if (amountUpdateTimer) {
			clearTimeout(amountUpdateTimer);
			amountUpdateTimer = null;
		}
	}

	onScopeDispose(cancelAmountUpdate);

	function onUpdate(value: number) {
		if (isTextItem.value || disabled.value || pending.value || amountUpdatePending.value) {
			stepperAmount.value = amount.value;
			return;
		}

		stepperAmount.value = value;
		isAmountDirty.value = true;
		scheduleAmountUpdate();
	}

	async function onRemove(onAfterRemove?: () => void) {
		if (isLocked.value) {
			return;
		}
		cancelAmountUpdate();
		isAmountDirty.value = false;
		await remove();
		onAfterRemove?.();
	}

	return {
		amount,
		pending,
		add,
		remove,
		currentItem,
		isTextItem,
		stepperAmount,
		amountUpdatePending,
		isLocked,
		onUpdate,
		onRemove,
		cancelAmountUpdate,
	};
}
